import type { LocalDate } from "@js-joda/core";
import type { BaseButtonStyle } from "components/designV2/buttons/baseButton";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { Checkbox } from "components/designV2/checkbox/checkbox";
import { Modal } from "components/designV2/modal/modal";
import { Text } from "components/designV2/text/text";
import { ColorV2 } from "config/colors";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type {
  TimeReportsV2Filter,
  TimeReportsV2Group,
} from "services/api/company/timeReports.service";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import type { TimeReportsGroupByOption } from "../../toolbar/timeReportsToolbar.component";
import { ActionCommentModal } from "./components/actionCommentModal.component";
import { TimeReportDetailsPanel } from "./components/detailsPanel/timeReportDetailsPanel.component";
import { PersonRowsList } from "./components/personRowsList.component";
import { PersonBulkActionsBar } from "./components/personBulkActionsBar.component";
import { usePersonBulkActions } from "./hooks/usePersonBulkActions";
import { usePersonSelection } from "./hooks/usePersonSelection";
import { useTimeReportsPersonView } from "./hooks/useTimeReportsPersonView";
import { resolveBulkActionModalCopy } from "./models/personView.bulkActionModal.logic";
import { formatSignedTotalMinutes } from "./models/personView.actions";
import { exportPersonRowToCsv } from "./personView.csvExport";
import { resolveTimeReportsDisplaySettings } from "web-apps/company/components/timeReportsV2/timeReports.settings.logic";
import {
  ContentWrap,
  DimmedContent,
  LoaderWrap,
  ListSelectAllRow,
  LoadingOverlay,
  LoadMoreSentinel,
  LoadMoreWrap,
  Message,
  RowList,
  Spinner,
  ViewContainer,
} from "./personView.styled";
import { ReportAbsenceModalV2 } from "web-apps/company/components/common/modals/reportAbsenceModalV2.component";

interface PersonViewProps {
  monthDate: LocalDate;
  groupByOption: TimeReportsGroupByOption;
  workGroupIds: number[];
  workerIds: number[];
  filters?: TimeReportsV2Filter[];
  dateRange: { fromDate: LocalDate; toDate: LocalDate } | null;
  isAllRowsExpanded: boolean;
  isPreferencesHydrated: boolean;
  queryScopeKey: string;
}

const APPROVE_BUTTON_STYLES: Partial<BaseButtonStyle> = {
  backgroundColor: ColorV2.PositiveGreen,
  hoverBackgroundColor: ColorV2.SimflexityGreen,
  activeBackgroundColor: ColorV2.SimflexityGreen,
  textColor: ColorV2.White,
  borderColor: "transparent",
  iconColor: ColorV2.White,
};

const DENY_BUTTON_STYLES: Partial<BaseButtonStyle> = {
  borderColor: ColorV2.NegativeRed,
  textColor: ColorV2.NegativeRed,
  iconColor: ColorV2.NegativeRed,
  hoverBackgroundColor: "rgba(211, 78, 78, 0.12)",
  activeBackgroundColor: "rgba(211, 78, 78, 0.2)",
};

export const PersonView = (props: PersonViewProps): JSX.Element => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();

  const viewState = useTimeReportsPersonView({
    monthDate: props.monthDate,
    groupByOption: props.groupByOption,
    workGroupIds: props.workGroupIds,
    workerIds: props.workerIds,
    filters: props.filters,
    dateRange: props.dateRange,
    isPreferencesHydrated: props.isPreferencesHydrated,
  });

  const selectionState = usePersonSelection({
    rows: viewState.rows,
    queryScopeKey: props.queryScopeKey,
  });

  const bulkActions = usePersonBulkActions({
    companyId: companyState.company?.id,
    selectedDetails: selectionState.selectedDetails,
    openedDetail: selectionState.openedDetail,
    selectedDetailIdSet: selectionState.selectedDetailIdSet,
    clearSelection: selectionState.clearSelection,
    retainSelectionByIds: selectionState.retainSelectionByIds,
    setOpenedDetail: selectionState.setOpenedDetail,
    reload: viewState.reload,
  });

  const displaySettings = useMemo(
    () => resolveTimeReportsDisplaySettings(companyState.companySettings),
    [companyState.companySettings],
  );

  const bulkActionModalCopy = useMemo(
    () =>
      resolveBulkActionModalCopy({
        action: bulkActions.bulkCommentAction,
        translate: t,
        approveButtonStyles: APPROVE_BUTTON_STYLES,
        denyButtonStyles: DENY_BUTTON_STYLES,
      }),
    [bulkActions.bulkCommentAction, t],
  );

  const showActionError = bulkActions.showActionError;

  const handleDownloadRowCsv = useCallback(
    (row: TimeReportsV2Group): void => {
      try {
        exportPersonRowToCsv(row, props.monthDate, t);
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown export error";
        const errorStack = error instanceof Error ? error.stack : undefined;
        const rowContext = {
          rowId: row.id,
          rowName: row.name,
          itemCount: row.items.length,
          itemIds: row.items.map((item) => item.id),
        };
        console.error("[TimeReportsV2][DownloadCsv] Export failed", {
          errorMessage,
          errorStack,
          monthDate: props.monthDate.toString(),
          rowContext,
        });
        showActionError();
      }
    },
    [props.monthDate, showActionError, t],
  );

  if (viewState.error) return <Message>{viewState.error}</Message>;

  const hasRows = viewState.rows.length > 0;
  const isOverlayLoading = viewState.isLoading && hasRows;

  if (viewState.isLoading && !hasRows) {
    return (
      <LoaderWrap>
        <Spinner />
      </LoaderWrap>
    );
  }

  if (!viewState.isLoading && !hasRows) {
    return <Message>{t("TimeReports.NoRows")}</Message>;
  }

  return (
    <ViewContainer>
      <ContentWrap>
        {isOverlayLoading && (
          <LoadingOverlay>
            <Spinner />
          </LoadingOverlay>
        )}
        <DimmedContent $dimmed={isOverlayLoading}>
          <RowList>
            <ListSelectAllRow>
              <Checkbox
                label={t("CompanyTimeReport.SelectAll")}
                checked={
                  selectionState.isAllRowsSelected
                    ? "checked"
                    : selectionState.isPartiallyRowsSelected
                      ? "indeterminate"
                      : "unchecked"
                }
                onChange={selectionState.toggleAllRowsSelection}
              />
            </ListSelectAllRow>
            <PersonRowsList
              rows={viewState.rows}
              groupByOption={props.groupByOption}
              queryScopeKey={props.queryScopeKey}
              isAllRowsExpanded={props.isAllRowsExpanded}
              deviationToleranceMinutes={
                displaySettings.deviationToleranceMinutes
              }
              includeFlexTimeDeviations={
                displaySettings.includeFlexTimeDeviations
              }
              selectedDetailIdSet={selectionState.selectedDetailIdSet}
              onDownloadRowCsv={handleDownloadRowCsv}
              onSelectionChange={selectionState.handleSelectionChange}
              onDetailClick={selectionState.setOpenedDetail}
              translate={t}
            />
            {viewState.isLoadingMore && (
              <LoadMoreWrap>
                <Spinner />
              </LoadMoreWrap>
            )}
            <LoadMoreSentinel ref={viewState.loadMoreRef} />
          </RowList>
        </DimmedContent>
      </ContentWrap>
      <PersonBulkActionsBar
        selectedCount={selectionState.selectedCount}
        totalTimeLabel={`${t("CompanyTimeReport.TotalTime")} ${formatSignedTotalMinutes(
          selectionState.totalSelectedMinutes,
        )}`}
        availableBulkActions={selectionState.availableBulkActions}
        canRunActions={bulkActions.canRunActions}
        isBulkActionLoading={bulkActions.isBulkActionLoading}
        denyButtonStyles={DENY_BUTTON_STYLES}
        approveButtonStyles={APPROVE_BUTTON_STYLES}
        onRunBulkAction={(action) => {
          void bulkActions.runBulkAction(action);
        }}
        translate={t}
      />
      {selectionState.openedDetail?.item.id != null && (
        <TimeReportDetailsPanel
          timeReportId={selectionState.openedDetail.item.id}
          onClose={() => selectionState.setOpenedDetail(null)}
          onActionCompleted={viewState.reload}
        />
      )}
      <ReportAbsenceModalV2
        isOpen={bulkActions.isAbsenceModalOpen}
        onClose={bulkActions.closeAbsenceModal}
        timeReportIds={bulkActions.absenceTargetIds}
        onReportAbsence={bulkActions.handleAbsenceReported}
      />
      {bulkActionModalCopy && bulkActions.bulkCommentAction && (
        <ActionCommentModal
          isOpen
          title={bulkActionModalCopy.title}
          confirmLabel={bulkActionModalCopy.confirmLabel}
          confirmStyleOverrides={bulkActionModalCopy.confirmStyleOverrides}
          isSubmitting={bulkActions.isBulkActionLoading}
          onClose={bulkActions.closeBulkCommentModal}
          onConfirm={async (comment) => {
            const action = bulkActions.bulkCommentAction;
            if (!action) return false;
            return bulkActions.runCommentableBulkAction(action, comment);
          }}
        />
      )}
      <Modal
        label={
          bulkActions.feedbackState?.type === "success" ||
          bulkActions.feedbackState?.type === "partial"
            ? t("Alerts.Updated")
            : t("Alerts.OhNo")
        }
        size="compact"
        centerText
        showCloseButton={false}
        isOpen={bulkActions.feedbackState !== null}
        onClose={bulkActions.clearFeedback}
        footer={
          <PrimaryButton
            label={t("DesignComponents.Close")}
            onClick={bulkActions.clearFeedback}
          />
        }
      >
        <Text typography="b2" align="center">
          {bulkActions.feedbackState?.type === "success" &&
            t("CompanyTimeReport.TimeReportHasBeenUpdated")}
          {bulkActions.feedbackState?.type === "partial" &&
            `${t("CompanyTimeReport.TimeReportHasBeenUpdated")} (${bulkActions.feedbackState.succeededCount}/${bulkActions.feedbackState.succeededCount + bulkActions.feedbackState.failedCount})`}
          {bulkActions.feedbackState?.type === "error" &&
            t("Alerts.SomethingWentWrong")}
        </Text>
      </Modal>
    </ViewContainer>
  );
};
